/** InstallProgress — timeline live poll of /history for the active op. */
import React, { useEffect, useState } from "react";
import { Card } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { useUpdater } from "../../hooks/useUpdater";

const FINAL_STATES = new Set(["installed", "install_failed", "rolled_back", "rollback_failed"]);
const LABELS = {
  install_started: "Démarrage de l'installation",
  backup_created: "Sauvegarde créée",
  files_swapped: "Fichiers permutés",
  migrations_applied: "Migrations appliquées",
  service_restarted: "Service redemarré",
  health_check_ok: "Health-check OK",
  installed: "Installation réussie",
  install_failed: "Échec de l'installation",
  rollback_started: "Démarrage du rollback",
  rolled_back: "Rollback terminé",
  rollback_failed: "Échec du rollback",
  uploaded: "Package reçu",
  validated: "Package validé",
  dry_run: "Simulation",
};

export default function InstallProgress({ operationId, onClose }) {
  const { getHistory, extractDetail } = useUpdater();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!operationId) return;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const data = await getHistory({ operationId, limit: 100 });
        if (cancelled) return;
        // API returns most recent first; render chronological
        setEvents([...data].reverse());
        const last = data[0]?.event_type;
        if (!FINAL_STATES.has(last)) {
          timer = setTimeout(poll, 2000);
        }
      } catch (err) {
        if (!cancelled) setError(extractDetail(err, "Impossible de récupérer la timeline."));
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [operationId, getHistory, extractDetail]);

  const last = events[events.length - 1]?.event_type;
  const isFinal = FINAL_STATES.has(last);

  return (
    <div className="space-y-4" data-testid="install-progress">
      <Alert variant={isFinal ? "default" : "destructive"} className={isFinal ? "" : "border-amber-400 bg-amber-50 text-amber-900"}>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="space-y-1">
          <p className="font-semibold">
            {isFinal ? (last === "installed" ? "Installation terminée" : last === "rolled_back" ? "Rollback terminé" : "Opération terminée (avec erreur)") : "Mise à jour en cours — ne fermez pas cette page."}
          </p>
          <p className="text-xs">Le processus continue même si vous fermez cet onglet. Cette page se resynchronise automatiquement à sa réouverture.</p>
        </AlertDescription>
      </Alert>
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      <Card className="p-4">
        <ol className="space-y-2" data-testid="timeline">
          {events.map((e, i) => (
            <li key={e.event_id} className="flex items-start gap-3">
              <StepIcon event_type={e.event_type} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium" data-testid={`event-${e.event_type}`}>{LABELS[e.event_type] || e.event_type}</span>
                  <span className="text-xs text-muted-foreground">{new Date(e.at).toLocaleTimeString()}</span>
                </div>
                {e.payload && Object.keys(e.payload).length > 0 && (
                  <details className="text-xs mt-1">
                    <summary className="cursor-pointer text-muted-foreground">payload</summary>
                    <pre className="bg-secondary/60 rounded p-2 overflow-auto">{JSON.stringify(e.payload, null, 2)}</pre>
                  </details>
                )}
              </div>
            </li>
          ))}
          {events.length === 0 && !error && (
            <li className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> En attente des premiers events…
            </li>
          )}
        </ol>
      </Card>
      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose} disabled={!isFinal} data-testid="btn-close-progress">Fermer</Button>
      </div>
    </div>
  );
}

function StepIcon({ event_type }) {
  if (["installed", "rolled_back", "health_check_ok", "baseline_seeded"].includes(event_type)) {
    return <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />;
  }
  if (["install_failed", "rollback_failed"].includes(event_type)) {
    return <XCircle className="h-4 w-4 text-red-600 mt-0.5" />;
  }
  return <Loader2 className="h-4 w-4 text-primary mt-0.5" />;
}
